import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { DataTable } from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import SearchInput from '@/components/SearchInput';
import { useMediaQuery, useShallowEqualSelector } from '@/hooks';
import { useHasBeenSubscribed } from '@/hooks/user';
import {
	resetDeleteDocumentState,
	setPage,
	setSearchTerm
} from '@/store/myDocuments';

import { DropdownMenuCell } from './DropdownMenuCell';
import { MobileCell } from './MobileCell';
import SelectionOptions from './SelectionOptions';
import Timer from './Timer';
import {
	ButtonsCell,
	NameCell,
	SelectCell,
	SelectColumn,
	SizeCell,
	UpdatedAtCell
} from './components';

function Table({ documents }) {
	const [rowSelection, setRowSelection] = useState({});
	const [grouping, setGrouping] = useState([]);

	const dispatch = useDispatch();
	const hasBeenSubscribed = useHasBeenSubscribed();
	const mdDown = useMediaQuery('mdDown');
	const { t } = useTranslation();

	const { deleteDocumentsCompleted, page, searchTerm, totalPages } =
		useShallowEqualSelector(({ myDocuments }) => ({
			deleteDocumentsCompleted: myDocuments.deleteDocuments.completed,
			page: myDocuments.page,
			searchTerm: myDocuments.searchTerm,
			totalPages: myDocuments.totalPages
		}));

	const resetRowSelection = () => {
		setRowSelection({});
	};

	const handlePageChange = newPage => {
		dispatch(setPage(newPage));
	};

	const ButtonsCellComponent = useCallback(
		row => <ButtonsCell row={row} />,
		[]
	);

	const TimerComponent = useCallback(
		row => <Timer createdAt={row.original?.createdAt} />,
		[]
	);

	const MobileCellComponent = useCallback(row => <MobileCell row={row} />, []);

	const columns = [
		{
			id: 'select',
			header: SelectColumn,
			cell: SelectCell,
			enableSorting: false,
			enableHiding: true
		},
		{
			accessorKey: 'name',
			header: t('myDocuments.table.name'),
			cell: NameCell
		},
		{
			accessorKey: 'updatedAt',
			header: t('myDocuments.table.updatedAt'),
			cell: UpdatedAtCell
		},
		{
			accessorKey: 'size',
			header: t('myDocuments.table.size'),
			cell: SizeCell
		},
		...(!hasBeenSubscribed
			? [
					{
						accessorKey: 'createdAt',
						header: t('myDocuments.table.timer'),
						cell: ({ row }) => TimerComponent(row)
					}
				]
			: []),
		{
			id: 'data',
			accessorKey: 'data',
			aggregationFn: 'unique',
			header: t('myDocuments.mobileHeader'),
			cell: ({ row }) => MobileCellComponent(row)
		},
		{
			accessorKey: 'buttons',
			header: '',
			cell: ({ row }) => ButtonsCellComponent(row)
		},
		{
			id: 'actions',
			cell: DropdownMenuCell
		}
	];

	useLayoutEffect(() => {
		if (mdDown) {
			setGrouping([
				'name',
				'updatedAt',
				'size',
				'createdAt',
				'buttons',
				'actions'
			]);
		} else {
			setGrouping(['data']);
		}
	}, [mdDown]);

	const getSelectedRowCount = () => {
		return Object.keys(rowSelection)
			.map(Number)
			.filter(i => !Number.isNaN(i)).length;
	};

	useEffect(() => {
		if (deleteDocumentsCompleted) {
			resetRowSelection();
			dispatch(resetDeleteDocumentState());
		}
	}, [deleteDocumentsCompleted]);

	return (
		<>
			<SearchInput
				name="search"
				type="text"
				className="w-full lg:w-[226px]"
				onChange={e => dispatch(setSearchTerm(e.target.value))}
				placeholder={t('myDocuments.search')}
			/>
			<DataTable
				columns={columns}
				data={documents}
				manualGrouping
				state={{
					searchTerm,
					rowSelection,
					grouping
				}}
				onGlobalFilterChange={value => dispatch(setSearchTerm(value))}
				onRowSelectionChange={setRowSelection}
				onGroupingChange={setGrouping}
			/>
			{totalPages > 1 && (
				<Pagination
					className="mt-2 justify-end"
					onChange={handlePageChange}
					page={page}
					totalPages={totalPages}
				/>
			)}

			<SelectionOptions
				rowSelection={rowSelection}
				reset={resetRowSelection}
				number={getSelectedRowCount()}
			/>
		</>
	);
}

export default Table;
